import Config from "~/config";
import wsId from "~/helpers/wsId";
import { normalizeHost } from "~portal/helpers/hostClassification";

const HANDOFF_SIGNING_CONTEXT = "worksuite-portal-session-handoff-v1";
const HANDOFF_STATE_MAX_AGE_MS = 30 * 60 * 1000;
const HANDOFF_STATE_CLOCK_SKEW_MS = 5 * 60 * 1000;

export interface PortalHandoffClaim {
	domain: string;
	issuedAt: number;
	nonce: string;
	returnTo: string;
}

export interface SignedPortalHandoffClaim extends PortalHandoffClaim {
	signature: string;
}

const textEncoder = new TextEncoder();

// lib.dom types TextEncoder output as ArrayBufferLike-backed, which SubtleCrypto's BufferSource parameter rejects.
const encodeUtf8 = (value: string): Uint8Array<ArrayBuffer> => textEncoder.encode(value) as Uint8Array<ArrayBuffer>;

const toBase64Url = (bytes: ArrayBuffer): string => {
	const view = new Uint8Array(bytes);
	let binary = "";
	for (let index = 0; index < view.byteLength; index++) {
		binary += String.fromCharCode(view[index]);
	}
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const importSigningKey = (): Promise<CryptoKey> =>
	crypto.subtle.importKey(
		"raw",
		encodeUtf8(`${HANDOFF_SIGNING_CONTEXT}:${Config.clientId}`),
		{ hash: "SHA-256", name: "HMAC" },
		false,
		["sign"]
	);

const signingPayload = (claim: PortalHandoffClaim): Uint8Array<ArrayBuffer> =>
	encodeUtf8(`${claim.domain}|${claim.returnTo}|${claim.issuedAt}|${claim.nonce}`);

const computeSignature = async (claim: PortalHandoffClaim): Promise<string> =>
	toBase64Url(await crypto.subtle.sign("HMAC", await importSigningKey(), signingPayload(claim)));

export const createPortalHandoffClaim = (domain: string, returnTo: string): PortalHandoffClaim => ({
	domain: normalizeHost(domain),
	issuedAt: Date.now(),
	nonce: wsId(),
	returnTo
});

export const signPortalHandoffClaim = async (claim: PortalHandoffClaim): Promise<SignedPortalHandoffClaim> => ({
	...claim,
	signature: await computeSignature(claim)
});

export const encodePortalHandoffClaim = (claim: SignedPortalHandoffClaim): string => JSON.stringify(claim);

export const decodePortalHandoffClaim = (encoded: string): unknown => {
	if (!encoded) return null;
	try {
		return JSON.parse(encoded);
	} catch {
		return null;
	}
};

const hasClaimShape = (value: unknown): value is SignedPortalHandoffClaim => {
	if (typeof value !== "object" || value === null) return false;
	const candidate = value as Record<string, unknown>;
	return (
		typeof candidate["domain"] === "string" &&
		candidate["domain"].length > 0 &&
		typeof candidate["returnTo"] === "string" &&
		typeof candidate["nonce"] === "string" &&
		candidate["nonce"].length > 0 &&
		typeof candidate["issuedAt"] === "number" &&
		Number.isFinite(candidate["issuedAt"]) &&
		typeof candidate["signature"] === "string" &&
		candidate["signature"].length > 0
	);
};

const isWithinFreshnessWindow = (issuedAt: number): boolean => {
	const age = Date.now() - issuedAt;
	return age <= HANDOFF_STATE_MAX_AGE_MS && age >= -HANDOFF_STATE_CLOCK_SKEW_MS;
};

const signaturesMatch = (expected: string, actual: string): boolean => {
	if (expected.length !== actual.length) return false;
	let difference = 0;
	for (let index = 0; index < expected.length; index++) {
		difference |= expected.charCodeAt(index) ^ actual.charCodeAt(index);
	}
	return difference === 0;
};

export const verifyPortalHandoffClaim = async (value: unknown): Promise<Maybe<SignedPortalHandoffClaim>> => {
	if (!hasClaimShape(value)) return null;
	if (!isWithinFreshnessWindow(value.issuedAt)) return null;
	const expected = await computeSignature(value);
	return signaturesMatch(expected, value.signature) ? value : null;
};

export default verifyPortalHandoffClaim;
